import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateInvoice, voidInvoice, fetchInvoicesByAppointmentId } from "../../store/thunks/invoiceThunk";
import CreateInvoice from "./CreateInvoice";
import { OWNER } from "../../constants/roles";
import { toast } from 'react-toastify';

export default function EditInvoice({ appointment, invoice }) {
  const dispatch = useDispatch();
  const createInvoiceRef = useRef(null);
  const user = useSelector((state) => state.auth.user);

  // Local state to track the status of the current operation
  const [localStatus, setLocalStatus] = useState("idle");
  const [voidStatus, setVoidStatus] = useState("idle");

  const handleSubmit = async () => {
    const invoiceData = createInvoiceRef.current?.getInvoiceData();
    if (!invoiceData) {
      console.error("No invoice data available");
      return;
    }

    setLocalStatus("loading");

    try {
      await dispatch(updateInvoice({ id: invoice._id, updateData: invoiceData }));
      setLocalStatus("succeeded");
    } catch (error) {
      console.error("Error creating invoice:", error);
      setLocalStatus("error");
    }
  };

  const handleVoid = async () => {
    setVoidStatus("loading");

    try {
      const result = await dispatch(voidInvoice({ invoiceId: invoice._id, reason: "Invoice voided by owner" }));
      if (result.meta.requestStatus === 'fulfilled') {
        setVoidStatus("succeeded");
        toast.success("Invoice voided successfully");
        // Refresh invoice data
        if (appointment?.client?._id) {
          dispatch(fetchInvoicesByAppointmentId(appointment._id));
        }
      } else {
        throw new Error(result.error?.message || "Failed to void invoice");
      }
    } catch (error) {
      console.error("[EditInvoice] Error voiding invoice:", error);
      setVoidStatus("error");
      toast.error("Failed to void invoice");
    }
  };

  // Check if user is owner
  const isOwner = user?.role?.includes("owner");

  return (
    <div>
      <div className="bg-white rounded-sm shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Edit Single Invoice</h3>
        <CreateInvoice ref={createInvoiceRef} appointment={appointment} isEditInvoice={true} />
      </div>
      <div className="flex gap-4 mt-4">
        <button
          onClick={handleSubmit}
          disabled={localStatus === "loading" || localStatus === "succeeded"}
          className={`px-4 py-2 rounded ${
            localStatus === "loading"
              ? "bg-gray-400"
              : localStatus === "succeeded"
              ? "bg-green-600 hover:bg-green-600"
              : localStatus === "idle"
              ? "bg-indigo-600 hover:bg-indigo-700"
              : "bg-red-600 hover:bg-red-700"
          } text-white`}
        >
          {localStatus === "loading"
            ? "Updating Invoice..."
            : localStatus === "succeeded"
            ? "Invoice Updated"
            : localStatus === "idle"
            ? "Update Invoice"
            : "Error"}
        </button>

        {isOwner && invoice?.status !== "void" && (
          <button
            onClick={handleVoid}
            disabled={voidStatus === "loading" || voidStatus === "succeeded"}
            className={`px-4 py-2 rounded ${
              voidStatus === "loading"
                ? "bg-gray-400"
                : voidStatus === "succeeded"
                ? "bg-gray-600"
                : "bg-red-600 hover:bg-red-700"
            } text-white`}
          >
            {voidStatus === "loading"
              ? "Voiding..."
              : voidStatus === "succeeded"
              ? "Voided"
              : "Void Invoice"}
          </button>
        )}
      </div>
    </div>
  );
}
